/* eslint-disable no-console */
import { createContext, useReducer, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { compactDecrypt } from "jose";
import axios from "axios";
import Logo from "../assets/images/logo.svg";

const UserStateContext = createContext();
const UserDispatchContext = createContext();

const userReducer = (state, action) => {
  switch (action.type) {
  case "LOGIN_SUCCESS":
    return { ...state, isAuthenticated: true };
  case "SIGN_OUT_SUCCESS":
    return { ...state, isAuthenticated: false, profile: {}, userRole: {}, subscription: {} };
  case "FETCH_PROFILE":
    return { ...state, isAuthenticated: true, profile: action.payload };
  case "GET_USER_ROLE":
    return { ...state, isAuthenticated: true, userRole: action.payload };
  case "GET_APP_LOGO":
    return { ...state, appLogo: action.payload };
  case "GET_APP_COLOR":
    return { ...state, appColor: action.payload };
  case "GET_SUBSCRIPTION":
    return { ...state, subscription: action.payload };
  default: {
    throw new Error(`Unhandled action type: ${action.type}`);
  }
  }
};

const UserProvider = ({ children }) => {
  const token = localStorage.getItem("shelf21-auth");
  const brand_color = localStorage.getItem("brand_color");
  const brand_logo = localStorage.getItem("brand_logo");
  const initialState = {
    isAuthenticated: !!localStorage.getItem("shelf21-auth"),
    userRole: {},
    profile: "",
    appColor: brand_color || "#0065FF",
    appLogo: brand_logo || Logo,
    subscription: {}
  };
  // const decoded = token ? decryptToken() : {};
  const [state, dispatch] = useReducer(userReducer, initialState);

  async function pemToCryptoKey(pem) {
    const binaryDer = atob(pem.replace(/(-----(BEGIN|END) PRIVATE KEY-----|\n)/g, ""));
    const binaryDerArray = Uint8Array.from(binaryDer, char => char.charCodeAt(0));

    return crypto.subtle.importKey(
      "pkcs8",
      binaryDerArray,
      {
        name: "RSA-OAEP",
        hash: "SHA-256"
      },
      true,
      ["decrypt"]
    );
  }

  const getUserRole = async () => {
    const privateKey = await pemToCryptoKey(process.env.REACT_APP_JWT_PRIVATE_KEY);
    const encryptedToken = token;
    try {
      const { plaintext } = await compactDecrypt(encryptedToken, privateKey);
      const decodedPayload = new TextDecoder().decode(plaintext);

      dispatch({ type: "GET_USER_ROLE", payload: JSON.parse(decodedPayload) });

      // return JSON.parse(decodedPayload);
    // eslint-disable-next-line no-empty
    } catch (error) { }
  };
  const fetchSubscription = () => {
    if (!token) return;

    axios
      .get(`${process.env.REACT_APP_API_URL}/api/v1/subscription/current`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      })
      .then((res) => {
        dispatch({ type: "GET_SUBSCRIPTION", payload: res?.data?.data || {} });
      })
      .catch((err) => {
        console.error("Error fetching subscription:", err);
        // Optionally dispatch an error state or empty subscription
        dispatch({ type: "GET_SUBSCRIPTION", payload: {} });
      });
  };

  const fetchUserProfile = () => {
    // --| Get User profile and pass it to context
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/v1/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      })
      .then((res) => {
        dispatch({ type: "FETCH_PROFILE", payload: res?.data?.data });
        dispatch({ type: "GET_APP_COLOR", payload: res?.data?.data?.branding?.brand_color });
        dispatch({ type: "GET_APP_LOGO", payload: res?.data?.data?.branding?.brand_logo?.file_url });
        if (res?.data?.data?.branding?.brand_color) {
          localStorage.setItem("brand_color", res?.data?.data?.branding?.brand_color);
        } else {
          localStorage.setItem("brand_color", "#0065FF");
        }
        if (res?.data?.data?.branding?.brand_logo?.file_url) {
          localStorage.setItem("brand_logo", res?.data?.data?.branding?.brand_logo?.file_url);
        } else {
          localStorage.setItem("brand_logo", null);
        }
      })
      .catch(() => {

      });
  };

  useEffect(() => {
    const isReferralRoute = window.location.pathname.startsWith("/referral")
      || window.location.pathname.startsWith("/auth/referrer");
    if (isReferralRoute) return;

    fetchUserProfile();
    getUserRole();
    fetchSubscription();
  }, []);

  return (
    <UserStateContext.Provider value={state}>
      <UserDispatchContext.Provider value={dispatch}>
        {children}
      </UserDispatchContext.Provider>
    </UserStateContext.Provider>
  );
};

const useUserState = () => {
  const context = useContext(UserStateContext);
  // eslint-disable-next-line no-undefined
  if (context === undefined) {
    throw new Error("useUserState must be used within a UserProvider");
  }

  return context;
};

const useUserDispatch = () => {
  const context = useContext(UserDispatchContext);
  // eslint-disable-next-line no-undefined
  if (context === undefined) {
    throw new Error("useUserDispatch must be used within a UserProvider");
  }

  return context;
};

const refreshSubscription = async (dispatch) => {
  const token = localStorage.getItem("shelf21-auth");
  if (!token) return;

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/v1/subscription/current`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );
    dispatch({ type: "GET_SUBSCRIPTION", payload: response?.data?.data || {} });

    return response?.data?.data;
  } catch (error) {
    console.error("Error refreshing subscription:", error);
    throw error;
  }
};

const handleProfileSelection = async (dispatch, profileId, navigate, setIsLoading) => {
  try {
    setIsLoading(true);

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/v1/auth/select-profile`,
      { profile_id: profileId },
      {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json"
        }
      }
    );

    if (response.data.token) {
      localStorage.setItem("shelf21-auth", response.data.token);
      localStorage.removeItem("multiple_profiles");
      localStorage.removeItem("user_email");

      dispatch({ type: "LOGIN_SUCCESS" });
      toast.success("Profile selected successfully!");

      setTimeout(() => {
        navigate("/dashboard");
        window.location.reload();
      }, 1000);
    } else {
      throw new Error("No token received after profile selection");
    }
  } catch (error) {
    if (error?.response) {
      if (error?.response?.status === 401) {
        const errorMessage = error?.response?.data?.message || "Session expired. Please login again.";
        toast.error(errorMessage);
        localStorage.removeItem("multiple_profiles");
        localStorage.removeItem("user_email");
        navigate("/auth/login");

        return;
      }

      toast.error(error?.response?.data?.message);
    } else if (error.request) {
      if (error.message.includes("Network Error") || error.message.includes("Failed to fetch")) {
        toast.error("Cannot connect to server. Please check your connection.");
      } else {
        toast.error("Network error. Please try again.");
      }
    } else {
      toast.error(error.message || "An error occurred");
    }

    localStorage.removeItem("multiple_profiles");
    localStorage.removeItem("user_email");
    navigate("/auth/login");
  } finally {
    setIsLoading(false);
  }
};

const handleLogin = async (
  dispatch,
  loginForm,
  navigate,
  setIsLoading,
  showValidationMessage,
  loginValidator
) => {
  if (loginValidator.allValid()) {
    setIsLoading(true);
    axios
      .post(`${process.env.REACT_APP_API_URL}/api/v1/auth/login`, loginForm, {
        withCredentials: true
      })
      .then(async (res) => {
        setIsLoading(false);
        if (res.data.multiple_profiles === true) {
          localStorage.setItem("multiple_profiles", JSON.stringify(res.data.data));
          localStorage.setItem("user_email", loginForm.email);
          toast.success(res?.data?.message);
          // eslint-disable-next-line no-console
          console.log("data", res?.data);
          setTimeout(() => {
            navigate("/auth/selectaccount");
          }, 1000);

          return;
        }
        localStorage.setItem("shelf21-auth", res?.data?.token);
        toast.success("Successfully Logged in");
        dispatch({ type: "LOGIN_SUCCESS" });
        setTimeout(() => {
          navigate("/dashboard");
          window.location.reload();
        }, 1000);
      })
      .catch((err) => {
        setIsLoading(false);
        const errorMessage = err?.response?.data?.message;
        const userId = err?.response?.data?.data?.id;
        if (errorMessage === "User not verified. Check email for verification link or contact support") {
          localStorage.setItem("userInfo", JSON.stringify({ verify: true, userId }));
          toast.error(errorMessage);
          setTimeout(() => {
            navigate(`/auth/verify/${userId}`);
          }, 1500);
        } else {
          toast.error(errorMessage);
        }
      });
  } else {
    showValidationMessage(true);
  }
};

const logOut = (dispatch, navigate) => {
  localStorage.removeItem("shelf21-auth");
  localStorage.removeItem("helpModalViewed");
  localStorage.removeItem("selected_ward");
  localStorage.removeItem("wards_list");
  setTimeout(() => {
    dispatch({ type: "SIGN_OUT_SUCCESS" });
  }, 1000);
  navigate("/auth/login");
};

export { UserProvider, useUserState, useUserDispatch, handleLogin, logOut, handleProfileSelection, refreshSubscription };
